import type { ServerKeyPublic, Website } from 'types';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useServerKeys } from '@/state/useServerKeys';
import { apiErrorMessage } from '@/state/api-error';
import { SERVER_KEYS_COPY } from './server-keys-copy';

/** The same limit create-server-key.ts enforces on a trimmed name. */
export const MAX_SERVER_KEY_NAME_LENGTH = 64;

/**
 * Everything the server keys dialog holds: whether it is open, the name being typed, which
 * confirm is showing, and the secret from the last create, which exists only in this state
 * and is gone once the dialog closes.
 *
 * The list and Revoke work whatever the visitor ID says: a key keeps working after the
 * visitor ID is turned off, so revoking it must stay possible. Only creating needs it on.
 */
export const useServerKeysDialog = (website: Website) => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [confirmingCreate, setConfirmingCreate] = useState(false);
  const [revoking, setRevoking] = useState<ServerKeyPublic | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Counts each open and close, so a create that answers after its dialog closed can tell
  // and drop the secret instead of showing it the next time the dialog opens.
  const session = useRef(0);
  const { keys, activeKeys, isLoading, isError, retry, create, revoke } = useServerKeys(website.id, isOpen);

  const visitorIdEnabled = !!website.visitorIdEnabled;
  const trimmedName = name.trim();
  const duplicateName = activeKeys.some((key) => key.name.toLowerCase() === trimmedName.toLowerCase());
  const nameError =
    trimmedName.length > MAX_SERVER_KEY_NAME_LENGTH
      ? SERVER_KEYS_COPY.nameTooLong
      : duplicateName
        ? SERVER_KEYS_COPY.nameTaken
        : null;
  const canCreate = visitorIdEnabled && !!trimmedName && !nameError;

  const open = () => {
    session.current += 1;
    setIsOpen(true);
  };

  /** Closing forgets the secret: it is shown once, and "once" ends with the dialog. */
  const close = () => {
    session.current += 1;
    setIsOpen(false);
    setName('');
    setSecret(null);
    setCopied(false);
    setConfirmingCreate(false);
    setRevoking(null);
  };

  const askCreate = () => canCreate && setConfirmingCreate(true);
  const cancelCreate = () => setConfirmingCreate(false);

  const confirmCreate = async () => {
    const startedIn = session.current;
    try {
      const created = await create(trimmedName);
      if (session.current !== startedIn) return;
      setSecret(created);
      setCopied(false);
      setName('');
      setConfirmingCreate(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Could not create the key.'));
    }
  };

  const askRevoke = (key: ServerKeyPublic) => setRevoking(key);
  const cancelRevoke = () => setRevoking(null);

  const confirmRevoke = async () => {
    if (!revoking) return;
    try {
      await revoke(revoking.id);
      toast.success(`Revoked "${revoking.name}"`);
      setRevoking(null);
    } catch (error) {
      // The list has been refreshed by now, so it shows whether the key is in fact revoked.
      toast.error(apiErrorMessage(error, 'Could not revoke the key.'));
      setRevoking(null);
    }
  };

  const copySecret = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      toast.error(SERVER_KEYS_COPY.copyFailed);
    }
  };

  return {
    website,
    isOpen,
    open,
    close,
    visitorIdEnabled,
    keys,
    isLoading,
    isError,
    retry,
    name,
    setName,
    nameError,
    canCreate,
    confirmingCreate,
    askCreate,
    cancelCreate,
    confirmCreate,
    revoking,
    askRevoke,
    cancelRevoke,
    confirmRevoke,
    secret,
    copied,
    copySecret,
  };
};

export type ServerKeysDialogManager = ReturnType<typeof useServerKeysDialog>;
